/**
 * Minimal, centralized client-side error reporting.
 *
 * Every catch block that isn't already surfacing a friendly message to
 * the user (via component state) should report through here instead
 * of calling console.error directly - one place to keep the format
 * consistent, and the only place that would need to change to wire in
 * a real error-tracking service (Sentry, LogRocket, ...) later without
 * touching every call site.
 *
 * Environment-aware (REACT_APP_ENVIRONMENT, see .env.example):
 *   - development/test (the default): logs to the browser console, so
 *     errors stay visible while building/debugging locally.
 *   - production: does NOT log to the console (a real end user's
 *     console shouldn't fill up with internal error noise) - this is
 *     the one place a real reporting service would be plugged in.
 *
 * `context` is a short, stable label for where the error happened
 * (e.g. "websocket", "ai_unavailable") - never include tokens,
 * passwords, or raw audio/transcript content in either argument.
 */
import { captureReportedError } from './monitoring';

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';

/**
 * `extra` (optional) may only carry the allowlisted fields of
 * services/monitoringScrub.js - e.g. the error boundary's componentStack or
 * a `stage` label. Remote reporting (services/monitoring.js) is a no-op
 * unless the build has REACT_APP_SENTRY_DSN; it sends the context and the
 * error's CATEGORY, never its message.
 */
export function reportError(context, error, extra) {
  captureReportedError(context, error, extra);

  if (ENVIRONMENT === 'production') {
    // Remote reporting happened above (when configured). Intentionally
    // silent otherwise - never spam an end user's console.
    return;
  }

  // eslint-disable-next-line no-console
  console.error(`[${context}]`, error);
}

/**
 * Non-error breadcrumb logging (connection lifecycle, capture pipeline
 * status, ...) - the console.log equivalent of reportError above. Same
 * environment gating: visible in development/test, completely silent in
 * production, so a real end user's console never fills up with internal
 * debug noise. `message` must be a plain, pre-formatted string - never
 * pass tokens, passwords, raw audio, or transcript/answer text.
 */
export function logDebug(context, message) {
  if (ENVIRONMENT === 'production') {
    return;
  }

  // eslint-disable-next-line no-console
  console.log(`DEBUG: [${context}] ${message}`);
}
